import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatMoney, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Utensils, ArrowLeft, User as UserIcon } from "lucide-react";

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return d.toLocaleDateString("en-US", { day: "2-digit", month: "short", year: "2-digit" });
};

export default function EmployeeFoodLedger() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/employees/${id}/food-ledger`);
        setData(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [id]);

  if (loading) return <div className="card-flat p-6 text-center text-gray-400">Loading…</div>;
  if (!data) return <div className="card-flat p-6 text-center text-gray-400">Not found</div>;

  const { employee, rows, total_food, count } = data;

  return (
    <div className="space-y-6" data-testid="food-ledger-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to={`/employees/${id}/ledger`} className="small-label text-gray-500 inline-flex items-center gap-1 hover:text-black">
            <ArrowLeft size={12} /> Salary ledger
          </Link>
          <div className="flex items-center gap-2 mt-1">
            <Utensils size={20} className="text-[#B45309]" />
            <h2 className="font-heading font-black text-2xl tracking-tight">Food Ledger</h2>
          </div>
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-600">
            <UserIcon size={14} className="text-gray-400" />
            <span className="font-semibold">{employee.name}</span>
            {employee.role && <span className="text-gray-400">· {employee.role}</span>}
          </div>
          <div className="text-xs text-gray-500 mt-1 max-w-2xl">
            This ledger tracks only <b>food</b> money spent for this employee. It is <b>completely separate</b> from
            the salary ledger — food entries never affect salary payable / paid balances.
          </div>
        </div>
        <div className="card-flat px-5 py-4 bg-[#FEF3C7]/50 border-[#FDE68A]">
          <div className="small-label text-[#B45309]">Total food consumed</div>
          <div className="data-num text-2xl font-black text-[#B45309] mt-1" data-testid="food-total">₹ {formatMoney(total_food || 0)}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">{count} entries</div>
        </div>
      </div>

      <div className="card-flat">
        <div className="table-wrap">
          <table className="table-dense w-full">
            <thead>
              <tr>
                <th className="text-left">Date</th>
                <th className="text-left">Category</th>
                <th className="text-left">Description</th>
                <th className="num">Amount ₹</th>
                <th className="num">Running total</th>
                <th className="text-left">Entered by</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr><td colSpan="6" className="py-10 text-center text-gray-400 text-sm">
                  No food entries yet for this employee.
                </td></tr>
              )}
              {rows.map((r) => (
                <tr key={r.expense_id} data-testid={`food-row-${r.expense_id}`}>
                  <td className="whitespace-nowrap">{fmtDate(r.date)}</td>
                  <td className="text-xs uppercase tracking-wider text-[#B45309] font-bold">{r.category}</td>
                  <td className="text-sm">{r.description || <span className="text-gray-400">—</span>}</td>
                  <td className="num data-num font-bold">₹ {formatMoney(r.amount)}</td>
                  <td className="num data-num text-[#B45309]">₹ {formatMoney(r.running_total)}</td>
                  <td className="text-xs text-gray-500">{r.created_by_email || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
